import _ from "lodash";
import { startTransition, useCallback } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import {
  activeViewIdAtomFamily,
  clearValuesFilterStoreObj,
  companyIdSelector,
  ConfirmResult,
  Log,
  RideCrudModeKey,
  tableFiltersSchemaAtomFamily,
  tableSearchAF,
  updateFinalSchemaLocalStore,
} from "@ryde/shared";

import { usePageContext } from "../contexts";
import { useModalConfirm } from "../hooks-singletons";
import { useCustomQueryParams } from "../navigation";
import { useDynamicTablesCleanup } from "./dynamic-tables";
import { usePreGlobalSearchCalendar } from "./use-pre-global-search-calendar";

export const useTableFiltersActions = () => {
  const { t } = useTranslation();
  const { entity } = usePageContext();
  const companyId = useRecoilValue(companyIdSelector);
  const { confirmDialogResult } = useModalConfirm();
  const [tableFiltersSchema, setTableFiltersSchema] = useRecoilState(
    tableFiltersSchemaAtomFamily(entity),
  );
  const setActiveViewId = useSetRecoilState(activeViewIdAtomFamily(entity));
  const setTableSearchValue = useSetRecoilState(tableSearchAF(entity));
  const { setFilterSearchQsByValue } = useCustomQueryParams();
  const { restoreSnapshotIfNeeded } = usePreGlobalSearchCalendar();
  const { resetPageOnFiltersChange, resetPageOnClearAll } =
    useDynamicTablesCleanup({ entity });

  const handleAddFilter = useEvent((filterId: number | undefined) => {
    if (!filterId) {
      return;
    }

    startTransition(() => {
      setTableFiltersSchema(prev => {
        const newFilters = { ...(prev ?? {}), [filterId]: null };
        Log.debug("Handle add filter, updated filters:", newFilters);
        setFilterSearchQsByValue(newFilters);

        if (companyId) {
          updateFinalSchemaLocalStore({
            companyId,
            page: entity,
            finalSchema: { filtersSchema: newFilters },
          });
        }

        return newFilters;
      });
      setActiveViewId(undefined);
    });
  });

  const handleClearFilters = useEvent(async () => {
    startTransition(() => {
      setTableSearchValue("");

      // Clear-all also drops id: global search — restore calendar overridden for that search.
      if (entity === RideCrudModeKey.uiUnit) {
        restoreSnapshotIfNeeded();
      }

      setTableFiltersSchema(prev => {
        const clearedObj = clearValuesFilterStoreObj(prev ?? {});
        Log.debug(
          "Clear filters handler called, updated store object:",
          clearedObj,
        );
        setFilterSearchQsByValue(clearedObj);

        if (companyId) {
          updateFinalSchemaLocalStore({
            companyId,
            page: entity,
            finalSchema: { filtersSchema: clearedObj },
          });
        }

        return clearedObj;
      });
      setActiveViewId(undefined);
      resetPageOnClearAll();
    });
  });

  const handleRemoveFilter = useEvent(
    async (filterIdToRemove: number, clearFilterInsteadIfValueExist = true) => {
      const isFilterExistAndNotEmpty =
        tableFiltersSchema && !_.isEmpty(tableFiltersSchema[filterIdToRemove]);

      /* prompt confirmation only if filter has value(s) */
      if (isFilterExistAndNotEmpty) {
        const message = clearFilterInsteadIfValueExist
          ? "general.CLEAR_FILTER_CONFIRM_MESSAGE"
          : "general.REMOVE_FILTER_CONFIRM_MESSAGE";

        const res = await confirmDialogResult({
          title: t("general.REMOVE_FILTER_CONFIRM"),
          message: t(message),
        });

        if (res !== ConfirmResult.OK) {
          Log.infoAndToast(
            t("errors.removeFilterCanceledByUser"),
            filterIdToRemove,
          );
          return;
        }
      }

      Log.debug(
        `Remove filter handler called for ${filterIdToRemove}, current filters: `,
        {
          ...(tableFiltersSchema ?? {}),
        },
      );
      startTransition(() => {
        setTableFiltersSchema(prev => {
          const updatedFilterObj =
            clearFilterInsteadIfValueExist && isFilterExistAndNotEmpty
              ? { ...(prev ?? {}), [filterIdToRemove]: null }
              : _.omit(prev ?? {}, String(filterIdToRemove));
          setFilterSearchQsByValue(updatedFilterObj);

          if (companyId) {
            updateFinalSchemaLocalStore({
              companyId,
              page: entity,
              finalSchema: { filtersSchema: updatedFilterObj },
            });
          }

          return updatedFilterObj;
        });
        setActiveViewId(undefined);

        // An empty filter narrowed nothing, so the rows and the page stay as they are.
        if (isFilterExistAndNotEmpty) {
          resetPageOnFiltersChange();
        }
      });
    },
  );

  const setValuesToFilter = useCallback(
    (fieldId: number, values: number[] | string[] | null[]) => {
      startTransition(() => {
        setTableFiltersSchema(prev => {
          const updatedFilter = {
            ...(prev ?? {}),
            [fieldId]: values,
          };
          Log.debug(
            `Set values to filter  called for field id ${fieldId}, updated filter:`,
            updatedFilter,
          );
          setFilterSearchQsByValue(updatedFilter);

          if (companyId) {
            updateFinalSchemaLocalStore({
              companyId,
              page: entity,
              finalSchema: { filtersSchema: updatedFilter },
            });
          }

          return updatedFilter;
        });
        setActiveViewId(undefined);
        resetPageOnFiltersChange();
      });
    },
    [
      companyId,
      entity,
      resetPageOnFiltersChange,
      setActiveViewId,
      setFilterSearchQsByValue,
      setTableFiltersSchema,
    ],
  );

  return {
    handleAddFilter,
    handleRemoveFilter,
    handleClearFilters,
    setValuesToFilter,
  };
};
